import { useState } from 'react';

import { Button } from '@/design-system';
import type { ReportShare } from '@/entities/report';
import { formatDayLong } from '@/shared/lib';

import { copyText } from '../browser';
import { reportCopy } from '../copy';
import { ReportStateCard } from './ReportStateCard';

export interface ReportLinkCardProps {
    /** De link van de versie die op dit scherm staat. */
    link: ReportShare | null;
    /** Is er al iets vanuit Smartkeur verstuurd? Zo niet, dan is kopiëren de weg. */
    sent: boolean;
    onOpenClientPage: () => void;
}

/**
 * De vaste rapportlink van deze keuring, met de knop om hem over te nemen.
 *
 * Eén keuring, één link: hij staat er zodra het rapport is vastgesteld — ook als
 * er nog niets vanuit Smartkeur is verstuurd — en hij verandert niet meer. Werk
 * je het rapport later bij, dan toont dezelfde link de nieuwste versie. Dat zegt
 * dit blok met zoveel woorden, want het is precies wat kantoor moet kunnen
 * vertrouwen om de link zelf in een e-mail te zetten.
 */
export function ReportLinkCard({ link, sent, onOpenClientPage }: ReportLinkCardProps) {
    const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
    const url = link?.url ?? null;

    return (
        <ReportStateCard
            tone="default"
            title={reportCopy.linkTitle}
            action={
                <>
                    {url ? (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={async () =>
                                setCopy((await copyText(url)) ? 'copied' : 'failed')
                            }
                        >
                            {copy === 'copied' ? reportCopy.copied : reportCopy.copyLink}
                        </Button>
                    ) : null}
                    <Button variant="outline" size="md" onClick={onOpenClientPage}>
                        {reportCopy.clientPage}
                    </Button>
                </>
            }
        >
            <p className="m-0">
                {url === null
                    ? reportCopy.linkWithoutUrl
                    : reportCopy.linkBody(formatDayLong(link?.validUntil ?? ''))}
            </p>
            {url ? (
                <p className="m-0 mt-2 font-[family-name:var(--font-mono)] text-[12px] leading-[1.45] break-all text-[var(--text-primary)] select-all">
                    {url}
                </p>
            ) : null}
            {!sent && url ? <p className="m-0 mt-2">{reportCopy.linkNotSentYet}</p> : null}
            {link?.accessCodeRequired ? (
                <p className="m-0 mt-2">{reportCopy.linkCodeRequired}</p>
            ) : null}
            {copy === 'failed' ? (
                <p
                    role="alert"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--warn-ink)]"
                >
                    {reportCopy.copyFailed}
                </p>
            ) : null}
        </ReportStateCard>
    );
}
